/**
 * @file deal-info.tsx
 * @description Сведения карточки сделки одной строкой (phase19.31, SC61):
 * «Сумма 7 500,00 ₽ · Срок — · Ответственный … · Клиент … · Воронка «…»».
 *
 * Заменяет три плитки («Текущая стадия», «Сумма», «SLA»): стадия уже стоит
 * пилюлей в шапке, подписи — по-русски («Срок», а не SLA; «Ответственный», а
 * не Owner), деньги — в «₽». Клиент — ссылка на карточку клиента, если у
 * сотрудника есть право смотреть клиентов.
 */

import { Link } from "react-router";

import type { InfoLineItem } from "../InfoLine";
import type { DealDto } from "../../../lib/api-crm";
import { dealDeadline, formatRub } from "../../../lib/crm-deal-view";

/** Ссылка-значение: цель касания 48px на телефоне, обычная строка с 1024. */
const LINK_CLASS = "inline-flex min-h-12 items-center text-primary underline-offset-2 hover:underline lg:min-h-0";

/** «Срок»: просрочка янтарным, иначе дата; закрытая сделка — прочерк. */
function deadlineItem(deal: DealDto): InfoLineItem {
  const state = dealDeadline(deal);
  if (state.kind === "overdue") {
    return { key: "deadline", label: "Срок", value: `просрочен на ${state.overdue} · ${state.until}`, tone: "attention" };
  }
  if (state.kind === "due") return { key: "deadline", label: "Срок", value: `${state.until} · осталось ${state.left}` };
  return { key: "deadline", label: "Срок", value: "—" };
}

export function buildDealInfo(deal: DealDto, opts: { customerHref: string | null }): InfoLineItem[] {
  const customerName = deal.customerDisplayName ?? "не указан";
  return [
    // Сумма — главное число сделки: вес 600, заметнее остальных сведений.
    { key: "amount", label: "Сумма", value: <span className="font-semibold">{formatRub(deal.expectedAmountGross)}</span> },
    deadlineItem(deal),
    { key: "owner", label: "Ответственный", value: deal.ownerDisplayName ?? "не назначен" },
    {
      key: "customer",
      label: "Клиент",
      value: opts.customerHref ? (
        <Link to={opts.customerHref} className={LINK_CLASS}>
          {customerName}
        </Link>
      ) : (
        customerName
      ),
    },
    { key: "pipeline", label: "Воронка", value: `«${deal.pipelineName}»` },
  ];
}
